import { Component, Inject, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { PlayerService } from './player.service';
import { PlayerModel, CreatePlayerRequest, UpdatePlayerRequest } from './player.type';
import { GOLD_RARITY_ID } from './player.constants';

export interface PlayerFormDialogData {
  player?: PlayerModel;
  mode: 'create' | 'edit';
}

@Component({
  selector: 'app-player-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatSnackBarModule,
  ],
  template: `
    <h2 mat-dialog-title>{{ isEditMode ? 'Edit Player' : 'Create Player' }}</h2>
    <mat-dialog-content>
      <form [formGroup]="playerForm" class="player-form">
        <mat-form-field appearance="outline">
          <mat-label>ID</mat-label>
          <input matInput type="number" formControlName="id" placeholder="Player ID" />
          @if (playerForm.get('id')?.hasError('required')) {
            <mat-error>ID is required</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Name</mat-label>
          <input matInput formControlName="name" placeholder="Player name" />
          @if (playerForm.get('name')?.hasError('required')) {
            <mat-error>Name is required</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Quality</mat-label>
          <mat-select formControlName="quality">
            <mat-option value="bronze">Bronze</mat-option>
            <mat-option value="silver">Silver</mat-option>
            <mat-option value="gold">Gold</mat-option>
            <mat-option value="special">Special</mat-option>
          </mat-select>
          @if (playerForm.get('quality')?.hasError('required')) {
            <mat-error>Quality is required</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Rarity ID</mat-label>
          <input
            matInput
            type="number"
            formControlName="rarity_id"
            placeholder="Rarity ID"
          />
          @if (playerForm.get('rarity_id')?.hasError('required')) {
            <mat-error>Rarity ID is required</mat-error>
          }
          @if (playerForm.get('rarity_id')?.hasError('min')) {
            <mat-error>Rarity ID must be at least 0</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Overall</mat-label>
          <input
            matInput
            type="number"
            formControlName="overall"
            placeholder="Overall rating"
          />
          @if (playerForm.get('overall')?.hasError('required')) {
            <mat-error>Overall is required</mat-error>
          }
          @if (playerForm.get('overall')?.hasError('min')) {
            <mat-error>Overall must be at least 0</mat-error>
          }
          @if (playerForm.get('overall')?.hasError('max')) {
            <mat-error>Overall cannot exceed 99</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Market Price</mat-label>
          <input
            matInput
            type="number"
            formControlName="market_price"
            placeholder="Market price"
          />
          @if (playerForm.get('market_price')?.hasError('required')) {
            <mat-error>Market price is required</mat-error>
          }
          @if (playerForm.get('market_price')?.hasError('min')) {
            <mat-error>Market price must be at least 0</mat-error>
          }
        </mat-form-field>

        <div class="checkbox-field">
          <mat-checkbox formControlName="active">Active</mat-checkbox>
        </div>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button
        mat-raised-button
        color="primary"
        (click)="onSubmit()"
        [disabled]="playerForm.invalid || isSubmitting"
      >
        {{ isEditMode ? 'Update' : 'Create' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .player-form {
        display: flex;
        flex-direction: column;
        gap: 16px;
        min-width: 400px;
        padding: 20px 0;
      }

      mat-form-field {
        width: 100%;
      }

      .checkbox-field {
        margin-top: 8px;
      }

      mat-dialog-content {
        overflow: visible;
      }
    `,
  ],
})
export class PlayerFormDialog {
  private _fb = inject(FormBuilder);
  private playerService = inject(PlayerService);
  private snackBar = inject(MatSnackBar);
  private dialogRef = inject(MatDialogRef<PlayerFormDialog>);

  isEditMode: boolean = false;
  isSubmitting: boolean = false;
  playerForm: FormGroup;

  constructor(@Inject(MAT_DIALOG_DATA) public data: PlayerFormDialogData) {
    this.isEditMode = data.mode === 'edit';

    this.playerForm = this._fb.group({
      id: [0, [Validators.required]],
      name: ['', [Validators.required]],
      quality: ['gold', [Validators.required]],
      rarity_id: [GOLD_RARITY_ID, [Validators.required, Validators.min(0)]],
      overall: [0, [Validators.required, Validators.min(0), Validators.max(99)]],
      market_price: [0, [Validators.required, Validators.min(0)]],
      active: [true],
    });

    if (this.isEditMode && data.player) {
      this.playerForm.patchValue({
        id: data.player.id,
        name: data.player.name,
        quality: data.player.quality,
        rarity_id: data.player.rarity_id,
        overall: data.player.overall,
        market_price: data.player.market_price,
        active: data.player.active,
      });
    }
  }

  onSubmit(): void {
    if (this.playerForm.invalid) {
      return;
    }

    this.isSubmitting = true;
    const formValue = this.playerForm.value;

    if (this.isEditMode && this.data.player) {
      const updateRequest: UpdatePlayerRequest = {
        name: formValue.name,
        quality: formValue.quality,
        rarity_id: formValue.rarity_id,
        overall: formValue.overall,
        market_price: formValue.market_price,
        active: formValue.active,
      };

      this.playerService.updatePlayer(this.data.player.id, updateRequest).subscribe({
        next: () => {
          this.snackBar.open('Player updated successfully', 'Close', {
            duration: 3000,
          });
          this.dialogRef.close(true);
        },
        error: (error) => {
          console.error('Error updating player:', error);
          this.snackBar.open('Error updating player', 'Close', {
            duration: 3000,
          });
          this.isSubmitting = false;
        },
      });
    } else {
      const createRequest: CreatePlayerRequest = {
        id: formValue.id,
        name: formValue.name,
        quality: formValue.quality,
        rarity_id: formValue.rarity_id,
        overall: formValue.overall,
        market_price: formValue.market_price,
        active: formValue.active,
      };

      this.playerService.createPlayer(createRequest).subscribe({
        next: () => {
          this.snackBar.open('Player created successfully', 'Close', {
            duration: 3000,
          });
          this.dialogRef.close(true);
        },
        error: (error) => {
          console.error('Error creating player:', error);
          this.snackBar.open('Error creating player', 'Close', {
            duration: 3000,
          });
          this.isSubmitting = false;
        },
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
